import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Compass, Star } from "lucide-react";
import { shop } from "../mock";
import { useCart } from "../context/CartContext";
import Reveal from "./Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const colorMap = {
  blue: "#187EE4",
  orange: "#FF6C10",
  red: "#FF1228",
  yellow: "#F1C91F",
};
const colorCycle = ["blue", "orange", "red", "yellow"];

const ShopSection = () => {
  const { addItem } = useCart();
  const [products, setProducts] = useState(shop.products);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/shop/products`)
      .then((res) => {
        const live = res.data?.products || [];
        if (active && live.length > 0) {
          setProducts(
            live.map((p, i) => ({ ...p, color: colorCycle[i % colorCycle.length] }))
          );
        }
      })
      .catch(() => {
        /* keep sample products */
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <section id="shop" className="bg-white py-16 md:py-24">
      {/* Banner */}
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <div className="rounded-[32px] overflow-hidden shadow-xl bg-white">
            <img
              src={shop.bannerImage}
              alt={shop.name}
              className="w-full h-auto block"
            />
          </div>
          <p className="mt-5 text-center text-base md:text-lg font-bold max-w-xl mx-auto" style={{ color: "#7a3d1f" }}>
            {shop.tagline}
          </p>
          <div className="mt-6 text-center">
            <span className="inline-block font-display text-xl md:text-2xl text-white px-7 py-2.5 rounded-full shadow-md" style={{ backgroundColor: "#FF6C10" }} data-testid="shop-coming-soon-message">
              Coming Soon
            </span>
            <p className="mt-3 text-slate-500 font-medium max-w-lg mx-auto text-sm md:text-base">
              Our Courage Canyon Outfitters gear is almost ready. Here&rsquo;s a sneak peek at what&rsquo;s on the way!
            </p>
          </div>
        </Reveal>

        {/* Product grid */}
        <div className="mt-10 md:mt-12 grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {products.map((p, i) => {
            const color = colorMap[p.color] || "#187EE4";
            return (
              <Reveal key={p.id} delay={(i % 4) * 80}>
                <div className="group h-full flex flex-col rounded-3xl bg-white border border-slate-100 shadow-sm hover:shadow-xl transition-shadow overflow-hidden">
                  <div className="relative aspect-square bg-slate-50 overflow-hidden">
                    <img
                      src={p.image}
                      alt={p.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    {p.tag && (
                      <span
                        className="absolute top-3 left-3 inline-flex items-center gap-1 font-display text-[11px] px-3 py-1 rounded-full text-white shadow"
                        style={{ backgroundColor: color }}
                      >
                        {p.tag === "Best Seller" && (
                          <Star className="w-3 h-3" fill="white" strokeWidth={0} />
                        )}
                        {p.tag}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col flex-1 p-4">
                    <h3 className="font-bold text-slate-800 text-sm md:text-base leading-snug min-h-[2.75rem]">
                      {p.name}
                    </h3>
                    {p.note && (
                      <p className="mt-1.5 text-[12px] leading-snug text-slate-500">
                        {p.note}
                      </p>
                    )}
                    <div className="mt-auto pt-3 flex items-center justify-end">
                      <button
                        onClick={() => addItem(p)}
                        className="inline-flex items-center gap-1.5 font-display text-white text-xs px-3.5 py-2 rounded-full transition-transform hover:scale-105 shadow-sm"
                        style={{ backgroundColor: color }}
                        aria-label={`Add ${p.name} to cart`}
                      >
                        <Plus className="w-4 h-4" strokeWidth={3} />
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <p className="mt-8 text-center text-slate-400 text-sm">
          Demo store — products are samples and no real orders are placed yet.
        </p>
      </div>
    </section>
  );
};

export default ShopSection;
